import type { StateCreator } from 'zustand';
import {
  type AdAccountEligibility,
  adAccountEligibility,
  INELIGIBLE_REASONS,
  selectedAdAccountOptions,
  selectionIneligibility,
  toAdAccountOptions
} from '../lib/adAccountEligibility';
import { buildTargets } from '../lib/buildCreateRequest';
import { deliveryBlockedReason } from '../lib/definitionDelivery';
import { type LoadError, loadErrorMessage, toLoadError } from '../lib/loadErrorMessage';
import { logError } from '../lib/logError';
import type {
  AdAccountsResponse,
  AdPlatform,
  ApiResult,
  AudienceDelivery,
  AudienceDestination,
  CustomAudiencesApiContext,
  LookalikeRatio,
  ValueMetric
} from '../types';
import { type LoadGuard, mayWrite } from './loadGuard';
import type { CustomAudiencesStore } from './useCustomAudiencesStore';
import type { TargetSelection } from './wizardTypes';

export const EMPTY_TARGETS: TargetSelection = {
  platform: 'meta',
  adAccountIds: [],
  lookalikeRatios: [],
  valueMetric: null
};

/* A 403 on the internal-only delivery list means "no delivery detail", not a failure */
const DELIVERIES_REFUSED_STATUS = 403;

const EMPTY_DESTINATIONS_DATA = {
  destinationsByDefinitionId: {} as Record<number, AudienceDestination[]>,
  destinationsErrorByDefinitionId: {} as Record<number, LoadError>,
  deliveriesBySnapshotId: {} as Record<number, AudienceDelivery[]>,
  deliveriesErrorBySnapshotId: {} as Record<number, LoadError>,
  adAccounts: null as AdAccountsResponse | null,
  adAccountsError: null as string | null,
  tosCheckByAdAccountId: {} as Record<string, TosCheck>
};

/**
 * The outcome of asking proxima-integrations to re-read a brand Meta account's custom audience terms.
 *
 * - `checking`: the request is in flight.
 * - `accepted`: Meta reports the terms accepted, and the account's `acceptedTos` is now true.
 * - `unaccepted`: Meta still reports them unaccepted.
 * - `error`: the request failed; `message` is the normalized error.
 */
export type TosCheck =
  | { status: 'checking' }
  | { status: 'accepted' }
  | { status: 'unaccepted' }
  | { status: 'error'; message: string };

export type DestinationsSlice = {
  destinationsByDefinitionId: Record<number, AudienceDestination[]>;
  /** Why a definition's destinations could not be loaded; the previous list is kept. */
  destinationsErrorByDefinitionId: Record<number, LoadError>;
  /** Delivery attempts per snapshot; empty when the internal-only list is refused. */
  deliveriesBySnapshotId: Record<number, AudienceDelivery[]>;
  deliveriesErrorBySnapshotId: Record<number, LoadError>;
  adAccounts: AdAccountsResponse | null;
  adAccountsError: string | null;
  /** The latest Meta terms re-check per brand Meta account; empty until one runs. */
  tosCheckByAdAccountId: Record<string, TosCheck>;
  targets: TargetSelection;

  /** Loads each definition's destinations; the write is dropped for another brand or a guard that is no longer current. */
  loadDestinations: (
    ctx: CustomAudiencesApiContext,
    definitionIds: number[],
    guard?: LoadGuard
  ) => Promise<void>;
  /** Loads each snapshot's deliveries; the write is dropped for another brand or a guard that is no longer current. */
  loadDeliveries: (
    ctx: CustomAudiencesApiContext,
    snapshotIds: number[],
    guard?: LoadGuard
  ) => Promise<void>;
  /**
   * Loads brand-owned and Proxima-owned ad accounts in parallel.
   *
   * One source failing keeps the other's accounts and names the failed source in `adAccountsError`.
   * Both failing leaves `adAccounts` as it was.
   * The write is dropped for another brand or a guard that is no longer current.
   * Then every brand Meta account that has not accepted Meta's terms, and has no re-check yet this session, is re-checked through `recheckAdAccountTos`.
   */
  loadAdAccounts: (ctx: CustomAudiencesApiContext, guard?: LoadGuard) => Promise<void>;
  /**
   * Re-reads one brand Meta account's custom audience terms through proxima-integrations and records the outcome in `tosCheckByAdAccountId`.
   *
   * An acceptance flips the account's `acceptedTos` in `adAccounts`, so it becomes eligible.
   * A re-check already in flight for the account is left alone.
   * The write is dropped for another brand or a guard that is no longer current.
   */
  recheckAdAccountTos: (
    ctx: CustomAudiencesApiContext,
    adAccountId: string,
    guard?: LoadGuard
  ) => Promise<void>;
  deliverySubmitting: boolean;
  /**
   * Delivers the definition to the selected targets; appends the new rows and clears the panel.
   *
   * A loaded definition that is paused or deleted returns a failed result with `DELIVERY_BLOCKED_REASONS` and never calls the API.
   * The selection is revalidated against the loaded ad accounts first.
   * An ineligible selection returns a failed result with the first reason and never calls the API.
   */
  addDestinations: (
    ctx: CustomAudiencesApiContext,
    definitionId: number
  ) => Promise<ApiResult<AudienceDestination[]>>;
  /** Switches platform and clears the picked ad accounts and value metric; picking the current one is a no-op. */
  setTargetPlatform: (platform: AdPlatform) => void;
  /**
   * Unpicks a picked account, or picks an account `adAccountEligibility` allows on the current platform.
   *
   * An ineligible or unloaded account leaves the selection unchanged and returns the reason.
   */
  toggleTargetAdAccount: (adAccountId: string) => AdAccountEligibility;
  toggleLookalikeRatio: (ratio: LookalikeRatio) => void;
  /** Sets the value metric, or null for standard audiences. */
  setTargetValueMetric: (valueMetric: ValueMetric | null) => void;
  resetTargets: () => void;
  /** Drops every loaded destination, delivery, and ad account, for a brand switch. */
  clearDestinationsData: () => void;
};

export const createDestinationsSlice: StateCreator<
  CustomAudiencesStore,
  [],
  [],
  DestinationsSlice
> = (set, get) => ({
  deliverySubmitting: false,
  ...EMPTY_DESTINATIONS_DATA,
  targets: EMPTY_TARGETS,

  loadDestinations: async (ctx, definitionIds, guard) => {
    const results = await Promise.all(
      definitionIds.map(async definitionId => ({
        definitionId,
        result: await ctx.api.listDestinations(ctx, { definitionId })
      }))
    );
    if (!mayWrite(get().loadedBrandId, ctx, guard)) return;
    set(state => {
      const next = { ...state.destinationsByDefinitionId };
      let errors = { ...state.destinationsErrorByDefinitionId };
      results.forEach(({ definitionId, result }) => {
        if (result.data) {
          next[definitionId] = result.data;
          errors = withoutKey(errors, definitionId);
          return;
        }
        errors[definitionId] = toLoadError(result.error, 'destinations');
        logError('load.destinations', { definitionId, error: result.error });
      });
      return { destinationsByDefinitionId: next, destinationsErrorByDefinitionId: errors };
    });
  },

  loadDeliveries: async (ctx, snapshotIds, guard) => {
    const results = await Promise.all(
      snapshotIds.map(async snapshotId => ({
        snapshotId,
        result: await ctx.api.listDeliveries(ctx, { snapshotId })
      }))
    );
    if (!mayWrite(get().loadedBrandId, ctx, guard)) return;
    set(state => {
      const next = { ...state.deliveriesBySnapshotId };
      let errors = { ...state.deliveriesErrorBySnapshotId };
      results.forEach(({ snapshotId, result }) => {
        if (result.data || result.error.status === DELIVERIES_REFUSED_STATUS) {
          next[snapshotId] = result.data ?? [];
          errors = withoutKey(errors, snapshotId);
          return;
        }
        errors[snapshotId] = { message: result.error.message, status: result.error.status };
        logError('load.deliveries', { snapshotId, error: result.error });
      });
      return { deliveriesBySnapshotId: next, deliveriesErrorBySnapshotId: errors };
    });
  },

  loadAdAccounts: async (ctx, guard) => {
    const [brand, proxima] = await Promise.all([
      ctx.api.listBrandAdAccounts(ctx),
      ctx.api.listProximaAdAccounts(ctx)
    ]);
    if (!mayWrite(get().loadedBrandId, ctx, guard)) return;
    if (brand.error) logError('load.brandAdAccounts', brand.error);
    if (proxima.error) logError('load.proximaAdAccounts', proxima.error);
    const errors = [
      brand.error ? `brand accounts: ${brand.error.message}` : null,
      proxima.error
        ? `Proxima-owned accounts: ${loadErrorMessage(proxima.error, 'proximaAdAccounts')}`
        : null
    ].filter(message => message != null);
    const adAccountsError = errors.length ? errors.join('; ') : null;
    if (!brand.data && !proxima.data) {
      set({ adAccountsError });
      return;
    }
    set({
      adAccounts: {
        metaAdAccountsList: brand.data?.metaAdAccountsList ?? [],
        tiktokAdAccountsList: brand.data?.tiktokAdAccountsList ?? [],
        proximaAdAccountsList: proxima.data ?? []
      },
      adAccountsError
    });
    const checks = get().tosCheckByAdAccountId;
    const unchecked = (brand.data?.metaAdAccountsList ?? []).filter(
      account => !account.properties?.acceptedTos && !checks[account.adAccountId]
    );
    await Promise.all(
      unchecked.map(account => get().recheckAdAccountTos(ctx, account.adAccountId, guard))
    );
  },

  recheckAdAccountTos: async (ctx, adAccountId, guard) => {
    if (get().tosCheckByAdAccountId[adAccountId]?.status === 'checking') return;
    const record = (check: TosCheck) =>
      set(state => ({
        tosCheckByAdAccountId: { ...state.tosCheckByAdAccountId, [adAccountId]: check }
      }));
    record({ status: 'checking' });
    const result = await ctx.api.checkAdAccountTos(ctx, adAccountId);
    if (!mayWrite(get().loadedBrandId, ctx, guard)) return;
    if (result.error) {
      logError('adAccounts.tos', { adAccountId, error: result.error });
      record({ status: 'error', message: result.error.message });
      return;
    }
    if (!result.data.acceptedTos) {
      record({ status: 'unaccepted' });
      return;
    }
    set(state => ({
      tosCheckByAdAccountId: {
        ...state.tosCheckByAdAccountId,
        [adAccountId]: { status: 'accepted' }
      },
      adAccounts: state.adAccounts && withTosAccepted(state.adAccounts, adAccountId)
    }));
  },

  addDestinations: async (ctx, definitionId) => {
    if (get().deliverySubmitting)
      return { error: { status: null, message: 'A delivery is already being submitted' } };
    const { adAccounts, targets, selectedDefinition, definitions } = get();
    const definition =
      selectedDefinition?.id === definitionId
        ? selectedDefinition
        : definitions.find(candidate => candidate.id === definitionId);
    const blocked = definition ? deliveryBlockedReason(definition.status) : null;
    if (blocked) return { error: { status: null, message: blocked } };
    const reason = selectionIneligibility(
      toAdAccountOptions(adAccounts),
      targets.platform,
      targets.adAccountIds
    );
    if (reason) return { error: { status: null, message: reason } };
    set({ deliverySubmitting: true });
    const result = await ctx.api.addDestinations(ctx, definitionId, buildTargets(targets));
    const inserted = result.data;
    if (inserted)
      set(state => ({
        destinationsByDefinitionId: {
          ...state.destinationsByDefinitionId,
          [definitionId]: [...(state.destinationsByDefinitionId[definitionId] ?? []), ...inserted]
        },
        targets: EMPTY_TARGETS
      }));
    set({ deliverySubmitting: false });
    return result;
  },

  setTargetPlatform: platform =>
    set(state =>
      state.targets.platform === platform
        ? state
        : { targets: { ...state.targets, platform, adAccountIds: [], valueMetric: null } }
    ),

  toggleTargetAdAccount: adAccountId => {
    const { adAccounts, targets } = get();
    if (!targets.adAccountIds.includes(adAccountId)) {
      const options = toAdAccountOptions(adAccounts);
      const option = options.find(candidate => candidate.adAccountId === adAccountId);
      if (!option) return { eligible: false, reason: INELIGIBLE_REASONS.unknownAccount };
      const eligibility = adAccountEligibility(
        option,
        targets.platform,
        selectedAdAccountOptions(options, targets.adAccountIds)
      );
      if (!eligibility.eligible) return eligibility;
    }
    set(state => ({
      targets: { ...state.targets, adAccountIds: toggle(state.targets.adAccountIds, adAccountId) }
    }));
    return { eligible: true };
  },

  toggleLookalikeRatio: ratio =>
    set(state => ({
      targets: { ...state.targets, lookalikeRatios: toggle(state.targets.lookalikeRatios, ratio) }
    })),

  setTargetValueMetric: valueMetric =>
    set(state => ({ targets: { ...state.targets, valueMetric } })),

  resetTargets: () => set({ targets: EMPTY_TARGETS }),

  clearDestinationsData: () => set({ ...EMPTY_DESTINATIONS_DATA, targets: EMPTY_TARGETS })
});

/* The brand Meta account with `acceptedTos` set, every other list untouched */
const withTosAccepted = (
  adAccounts: AdAccountsResponse,
  adAccountId: string
): AdAccountsResponse => ({
  ...adAccounts,
  metaAdAccountsList: adAccounts.metaAdAccountsList.map(account =>
    account.adAccountId === adAccountId
      ? {
          ...account,
          properties: { currency: account.properties?.currency ?? '', acceptedTos: true }
        }
      : account
  )
});

const withoutKey = <V>(record: Record<number, V>, key: number): Record<number, V> => {
  const { [key]: _removed, ...rest } = record;
  return rest;
};

const toggle = <T>(items: T[], item: T): T[] =>
  items.includes(item) ? items.filter(value => value !== item) : [...items, item];
